import { useEffect } from "react";

export interface TidioUserData {
  userId?: string;
  userName?: string;
  userEmail?: string;
}

// Declare Tidio types for window and document
declare global {
  interface Window {
    tidioChatApi?: {
      ready?: (() => void)[];
      setVisitorData?: (data: {
        distinct_id?: string;
        email?: string;
        name?: string;
        phone?: string;
        city?: string;
        country?: string;
        tags?: string[];
      }) => void;
      addVisitorTags?: (tags: string[]) => void;
    };
  }

  interface Document {
    tidioIdentify?: {
      distinct_id?: string | number;
      email?: string;
      name?: string;
      phone?: string;
    };
  }
}

/**
 * Custom hook to load the Tidio chat script when a user is authenticated
 * and configure it with user information following Tidio's API specification
 */
export const useTidioScript = (
  isUserAuthenticated: boolean,
  userData?: TidioUserData,
) => {
  useEffect(() => {
    if (!isUserAuthenticated) {
      return;
    }

    // Check if Tidio script is already loaded
    const existingScript = document.querySelector(
      'script[src*="code.tidio.co"]',
    );

    if (existingScript) {
      // If script exists but we have new user data, update Tidio configuration
      if (userData && window.tidioChatApi?.setVisitorData) {
        window.tidioChatApi.setVisitorData({
          distinct_id: userData.userId,
          email: userData.userEmail,
          name: userData.userName,
        });
      }
      return;
    }

    // Set initial visitor identification before loading the script
    // This is the recommended way according to Tidio documentation
    if (userData) {
      document.tidioIdentify = {
        distinct_id: userData.userId,
        email: userData.userEmail,
        name: userData.userName,
      };
    }

    // Create and append the Tidio script
    const script = document.createElement("script");
    script.src = "//code.tidio.co/j3sxyw3doiawkb4on6t1jyfqcaebkja0.js";
    script.async = true;
    document.head.appendChild(script);

    // Cleanup function to remove script if component unmounts
    return () => {
      const scriptToRemove = document.querySelector(
        'script[src*="code.tidio.co"]',
      );
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      // Clean up global Tidio objects
      if (window.tidioChatApi) {
        delete window.tidioChatApi;
      }
      if (document.tidioIdentify) {
        delete document.tidioIdentify;
      }
    };
  }, [isUserAuthenticated, userData]);
};
